import LandmarksAPI from '../api/landmarksid';
import { delay } from 'redux-saga'
import {all, call, takeLatest, takeEvery, put} from 'redux-saga/effects'
import { setOrganizations, setOrganizationsSummary, setOrganizationPois,
        fenceMapConcat, resetFenceMapFences } from './actions';

import createHistory from 'history/createHashHistory'

import {
  SAGA_LOAD_ORGANIZATIONS,
  SAGA_CREATE_ORGANIZATION,
  SAGA_GET_ORGANIZATION_POIS,
  SAGA_GET_ORGANIZATION,
  SAGA_LOAD_MAP_FENCES,
  SAGA_IMPORT_ADDRESSES,
  SAGA_IMPORT_POIS,
  SAGA_REMOVE_ORGANIZATION,
  SAGA_REMOVE_POIS,
} from './actions';

import {
  setOrganization,
  resetOrganization,
  updatePOIUploadStatus,
  poiUploadError,
} from './actions'

import {setError, resetErrors} from '../errors/actions';

const history = createHistory();

// Get organizations from the server
function* getOrganizations() {
  const results = yield call(LandmarksAPI.getOrganizations);
  const summary = yield call(LandmarksAPI.getPOISummary);

  if (summary.error === null) {
    yield put(setOrganizationsSummary(summary.json.body));
  }

  // Error checking the server response
  if (results.error === null){
    yield put(setOrganizations(results.json.body));
  } else {
    yield put({type: 'ERROR_IN_FETCHING_DATA'});
  }
}

function* createOrganization(action){
  if (action.payload.requestType === 'custom'){
    const results = yield call(LandmarksAPI.createOrganization, action.payload);

    if (results.error === null && results.json){
      const organizationId = results.json.body._id;
      history.push(`/organizations/${organizationId}/pois`);
    }else{
      yield put({type: 'ERROR_CREATING_DATA'});
    }
  }else{
    const results = yield call(LandmarksAPI.createOrganizationFromPlaces, action.payload);
    if (results.error === null && results.json){
      const organizationId = results.json.body._id;
      history.push(`/organizations/${organizationId}/pois`);
    }else{
      yield put({type: 'ERROR_CREATING_DATA'});
    }
  }
}

function* getOrganizationPois(action){
  yield delay(1000);
  const results = yield call(LandmarksAPI.getOrganizationPois, 
                            action.payload.organizationId, action.payload.searchTerm,
                            action.payload.page);
  if (results.error === null && results.json){
    yield put(setOrganizationPois(action.payload.organizationId, results.json.body, action.payload.page));
  } else {
    history.push('/organizations');
  }
}

function* getOrganization(action){
  const results = yield call(LandmarksAPI.getOrganization, action.payload.organizationId);
  if (results.error === null && results.json){
    yield put(setOrganization(results.json.body));
  } else {
    history.push('/organizations');
  }
}

function* removeOrganization(action){
  const results = yield call(LandmarksAPI.removeOrganization, action.payload.organizationId);
  if (results.error === null) {
    yield put({ type: SAGA_LOAD_ORGANIZATIONS });
    history.push('/organizations');
  } else {
    yield put(setError(results.json.message));
    yield delay(4000);
    yield put(resetErrors());
  }
}

function* removePOI(poiId){
  yield call(LandmarksAPI.removePOI, poiId);
}

function* removePOIs(action){
  const allCalls = [];
  for (let i = 0; i < action.payload.poiIds.length; i += 1) {
    allCalls.push(yield call(removePOI, action.payload.poiIds[i]));
  }

  // Wait for all the calls to finish
  yield all(allCalls);

  // Reset organization
  yield put(resetOrganization(action.payload.organizationId));

  // Grab organization and organizationPois
  yield call(getOrganization, action);
  yield call(getOrganizationPois, action);
}

// Refactor this
function* getMapFences(action){
  if (action.payload.cancelRequests){
    yield put(resetFenceMapFences());
    return;
  }

  if (action.payload.pageNumber === 0){
    yield put(resetFenceMapFences());
    yield call(delay, 100);
  }

  if (action.payload.pageNumber === 20){
    return;
  }

  const results = yield call(LandmarksAPI.getMapFences, action.payload);
  if (results.error === null && results.json){
    const fences = results.json.body;
    if (action.payload.pageNumber === 0){
      yield put(resetFenceMapFences());
    }
    yield put(fenceMapConcat(results.json.body));
    if (fences.length >= 1000){
      yield put({...action, payload: {...action.payload, pageNumber: action.payload.pageNumber + 1}})
    }
  }
}

function* serverImportAddresses(action){
  const limit = 20;
  const page = action.payload.page || 0;
  const addresses = action.payload.addresses.slice(page*limit, (page+1)*limit);

  if (addresses.length === 0) {
    yield delay(1500);

    // Grab organization and organizationPois
    yield call(getOrganization, action);
    yield call(getOrganizationPois, action);
    return;
  }

  // Reset organization
  yield put(resetOrganization(action.payload.organizationId));

  const results = yield(call(LandmarksAPI.importAddresses, action.payload.organizationId, addresses));
  if (results.error === null && results.json){
    // Import addresses
    yield put({
      type: SAGA_IMPORT_ADDRESSES,
      payload: {
        organizationId: action.payload.organizationId,
        addresses: action.payload.addresses,
        page: page + 1,
      }
    });
  } else {
    yield put(setError(results.json.message));
    yield delay(4000);
    yield put(resetErrors());
  }
}

function* serverImportPOIs(action){
  const limit = 5;
  const page = action.payload.page || 0;
  const pois = action.payload.pois.slice(page*limit, (page+1)*limit);
  const totalPages = Math.ceil(action.payload.pois.length/limit);

  yield put(updatePOIUploadStatus(totalPages, page));

  if (page === 0) {
    history.push(`/organizations/uploadStatus`);
  }

  if (pois.length === 0) return;

  const results = yield call(LandmarksAPI.importPOIs, 
                                  action.payload.organizationId, 
                                  pois);

  // Move to next step

  yield put({
    type: SAGA_IMPORT_POIS,
    payload: {
      organizationId: action.payload.organizationId,
      pois: action.payload.pois,
      page: page + 1,
    }
  });

  if (!results.error === null) {
    yield put(poiUploadError(results.json.body.failed));
    yield put(setError(results.json.message));
    yield delay(4000);
    yield put(resetErrors());
  }
}

// Server functions

export default function* organizationsSaga(){
	yield all([
		takeLatest(SAGA_LOAD_ORGANIZATIONS, getOrganizations),
    takeEvery(SAGA_CREATE_ORGANIZATION, createOrganization),
    takeLatest(SAGA_GET_ORGANIZATION_POIS, getOrganizationPois),
    takeEvery(SAGA_GET_ORGANIZATION, getOrganization),
    takeLatest(SAGA_LOAD_MAP_FENCES, getMapFences),
    takeLatest(SAGA_IMPORT_ADDRESSES, serverImportAddresses),
    takeLatest(SAGA_IMPORT_POIS, serverImportPOIs),
    takeLatest(SAGA_REMOVE_ORGANIZATION, removeOrganization),
    takeLatest(SAGA_REMOVE_POIS, removePOIs),
	]);
}


// WEBPACK FOOTER //
// ./src/organizations/sagas.js